import * as React from 'react';
import FileDropTarget from 'components/common/fileDropTarget';
import Modal from 'components/common/modal';
import { killEvent } from 'utils/domUtils';
import strings from 'utils/strings';
import UserFileParser from 'utils/userFileParser';
import UserDetails from 'types/userDetails';
import { buildClassString, replaceTokens } from '@ps/payscale-shared-ui/lib/StringUtils';
import './addUsersFromFileModal.scss';

interface AddUsersFromFileModalProps {
    onCommit?: (users: UserDetails[], sendInvite: boolean) => void; // callback to call when ok button clicked
    onHide?: () => void;                       // callback to notify parent when modal hidden
    show: boolean;                             // whether or not to show modal
    sendInvite?: boolean;                      // whether or not to send invite email immediately
    showInviteDropdown: boolean;
}

// exported for testing
export interface AddUsersFromFileModalState {
    invalidFile: boolean;        // whether or not given an invalid file
    parsedUsers?: UserDetails[];  // list of users parsed from file
    visible: boolean;            // whether or not modal is visible
    selectedFile: string;
    sendInvite: boolean;         // whether or not to send invite email immediately
}

class AddUsersFromFileModal extends React.PureComponent
    <AddUsersFromFileModalProps, AddUsersFromFileModalState> {

    private _dropTargetRef: FileDropTarget | null;

    constructor(props: AddUsersFromFileModalProps) {
        super(props);
        this.state = Object.assign(
            {},
            this.state,
            {
                invalidFile: false,
                parsedUsers: null,
                visible: props.show,
                selectedFile: '',
                sendInvite: props.sendInvite ? props.sendInvite : true
            }
        );
        this._hide = this._hide.bind(this);
        this._onBrowseClick = this._onBrowseClick.bind(this);
        this._onCommit = this._onCommit.bind(this);
        this._onFileSelect = this._onFileSelect.bind(this);
        this._onSendInviteChange = this._onSendInviteChange.bind(this);
    }

    // sync our internal state with props
    UNSAFE_componentWillReceiveProps(nextProps: AddUsersFromFileModalProps): void {
        // if transitioning from hidden to shown, clear out previous values
        if (!this.state.visible && nextProps.show) {
            this.setState({
                invalidFile: false,
                selectedFile: '',
                visible: nextProps.show,
                sendInvite: true
            });
        } else {
            this.setState({ visible: nextProps.show });
        }
    }

    render(): React.ReactNode {
        const { visible, parsedUsers } = this.state;

        return (
            <Modal className="add-users-modal" show={visible} onHide={this._hide}>
                <h2 className="add-users-modal__title">{strings.addUsersFromFile}</h2>
                <div className="add-users-modal__content">
                {
                    this.props.showInviteDropdown ?
                        <div className="send-invite">
                            <label htmlFor="sendInvite" className="add-users-modal__label">
                                {strings.sendEmail}
                            </label>
                            <select
                                defaultValue={strings.sendNow}
                                id="sendInvite"
                                className="pxl-form-control add-users-modal__select"
                                onChange={this._onSendInviteChange}
                            >
                                {[
                                    <option
                                        value={'true'}
                                        key={'now'}
                                    >
                                            {strings.sendNow}
                                    </option>,
                                    <option
                                        value={'false'}
                                        key={'later'}
                                    >
                                            {strings.sendLater}
                                    </option>
                                ]}
                            </select>
                        </div> : null
                }
                <FileDropTarget
                    allowedExtensions={['.csv']}
                    className="add-users-modal__droptarget"
                    dragOverClassName="drag-over"
                    ref={ref => this._dropTargetRef = ref}
                    onFileSelect={this._onFileSelect}
                >
                    <div className="add-users-modal__drag-drop-text">
                        {strings.dropFile}
                    </div>
                    <button
                        className="pxl-btn-sm pxl-btn-secondary add-users-modal__upload-cta"
                        onClick={this._onBrowseClick}
                    >
                        {strings.browse}
                    </button>
                </FileDropTarget>
                <div className="add-users-modal__file-container">
                    {this._renderStatus()}
                </div>
            </div>
            <div className="modal__button-container">
                <a className="modal__cancel-link" href="#" onClick={this._hide}>{strings.cancel}</a>
                <button
                    className="pxl-btn pxl-btn-primary modal__ok-button"
                    onClick={this._onCommit}
                    disabled={!parsedUsers}
                >
                    {strings.continue}
                </button>
            </div>
        </Modal>
        );
    }

    // Render status message -- either parsed users, file's invalid, etc
    private _renderStatus(): React.ReactNode {
        const { invalidFile, parsedUsers, selectedFile } = this.state;
        if (!invalidFile && !parsedUsers) {
            return null;
        }

        const classString = buildClassString({
            'add-users-modal__status': true,
            'error': invalidFile
        });

        return (
            <div className={classString}>
                {invalidFile ?
                    strings.invalidFileMsg :
                    replaceTokens(strings.bulkUploadFile, selectedFile, parsedUsers?.length ?? 0)}
            </div>
        );
    }

    // Hide the dialog/handle click on close button or overlay
    private _hide(e?: React.SyntheticEvent<HTMLElement>): void {
        if (e) {
            killEvent(e);
        }
        this.setState({ visible: false });

        const { onHide } = this.props;
        if (onHide) {
            onHide();
        }
    }

    // Handle click on 'browse' button by bringing up the
    // file input's browse dialog
    private _onBrowseClick(e: React.SyntheticEvent<HTMLElement>): void {
        killEvent(e);
        this._dropTargetRef?.showFilePicker();
    }

    private _onCommit(): void {
        const { onCommit } = this.props;
        const { parsedUsers, sendInvite } = this.state;
        if (onCommit) {
            onCommit(parsedUsers ?? [], sendInvite);
        }

        this._hide();
    }

    // Handle selection of file from the dialog
    // NOTE that while a list of files may be provided, this method
    // will only consider the first in the list, and check that it matches the correct
    // format
    private _onFileSelect(files: File[]): void {
        if (!files || files.length === 0 || files[0].name.endsWith('.csv') === false) {
            this.setState({
                invalidFile: true,
                selectedFile: ''
            });
        } else {
            this._loadFile(files[0]);
        }
    }

    // Load the contents of the given file, either via
    // file dialog or drag + drop
    private _loadFile(file: File): void {
        if (!file) {
            return;
        }

        this.setState({
            invalidFile: false,
            selectedFile: file.name
        });

        const reader = new FileReader();
        reader.onload = () => this._onFileLoad(reader.result as string);
        reader.readAsText(file);
    }

    // Process contents of file after loading from disk
    private _onFileLoad(contents: string): void {
        const parser = new UserFileParser(contents);
        if (!parser.isValid()) {
            this.setState({
                invalidFile: true
            });
        } else {
            this.setState({
                invalidFile: false,
                parsedUsers: parser.getUsers()
            });
        }
    }

    private _onSendInviteChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({
            sendInvite: e.target.value === 'true'
        });
    }
}

export { AddUsersFromFileModal };
export default AddUsersFromFileModal;



// WEBPACK FOOTER //
// ./src/components/addUsersFromFileModal.tsx